import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { formatCustomizationMoney } from '../../api/customizationsAPI';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { getOfferingVertical } from './offeringCustomizationVertical';

const Page = styled.main`max-width: 680px; margin: 0 auto; padding: 2rem 1rem 5rem;`;
const Back = styled(Link)`color: #0f3d36; display: inline-block; font-weight: 700; margin-bottom: 1.25rem;`;
const Title = styled.h1`color: var(--text-primary); font-size: 2rem; margin: 0 0 1.25rem;`;
const Summary = styled.section`background: #e7f4ef; border-radius: 0.75rem; margin-bottom: 1.5rem; padding: 1rem;`;
const SummaryTitle = styled.h2`color: #0f3d36; font-size: 1rem; margin: 0 0 0.4rem;`;
const Group = styled.div`margin-top: 1.25rem;`;
const Label = styled.label`color: var(--text-primary); display: block; font-weight: 800; margin-bottom: 0.45rem;`;
const Input = styled.input`
  border: 1.5px solid ${(p) => (p.$error ? '#dc2626' : 'var(--border-color, #d1d5db)')};
  border-radius: 0.55rem; box-sizing: border-box; font: inherit; padding: 0.8rem; width: 100%;
`;
const Textarea = styled.textarea`
  border: 1.5px solid var(--border-color, #d1d5db); border-radius: 0.55rem;
  box-sizing: border-box; font: inherit; min-height: 120px; padding: 0.8rem; resize: vertical; width: 100%;
`;
const Helper = styled.p`color: var(--text-secondary); font-size: 0.8rem; line-height: 1.45; margin: 0.4rem 0 0;`;
const Error = styled.p`color: #b91c1c; font-size: 0.8rem; margin: 0.35rem 0 0;`;
const Note = styled.p`color: var(--text-secondary); line-height: 1.5; margin: 1.5rem 0;`;
const Actions = styled.div`display: flex; gap: 0.75rem;`;
const Primary = styled.button`
  background: #0f3d36; border: 0; border-radius: 0.55rem; color: #fff; cursor: pointer;
  font-weight: 800; padding: 0.85rem 1.2rem; &:disabled { cursor: not-allowed; opacity: 0.55; }
`;
const Cancel = styled(Link)`color: var(--text-secondary); font-weight: 700; padding: 0.85rem; text-decoration: none;`;

export function OfferingCustomizationQuotePage({ verticalKey = 'boutique' }) {
  const vertical = getOfferingVertical(verticalKey);
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [price, setPrice] = useState('');
  const [deliveryDays, setDeliveryDays] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  useEffect(() => {
    vertical.getRequest(requestId)
      .then((data) => {
        setRequest(data);
        const quote = data.active_quote || data.quotes?.[0];
        if (quote) {
          setPrice(String(Number(quote.amount_cents) / 100));
          setDeliveryDays(String(quote.delivery_days));
          setMessage(quote.message || '');
        }
      })
      .catch(() => toast.error('Something went wrong. Try again.'))
      .finally(() => setLoading(false));
  }, [requestId, vertical]);

  const submit = async (event) => {
    event.preventDefault();
    const amount = Number(price);
    const days = Number(deliveryDays);
    const nextErrors = {};
    if (!Number.isFinite(amount) || amount <= 0) nextErrors.price = 'Enter a price greater than 0.';
    if (!Number.isInteger(days) || days <= 0) nextErrors.deliveryDays = 'Enter delivery days greater than 0.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSending(true);
    try {
      await vertical.sendQuote(requestId, {
        amount_cents: Math.round(amount * 100),
        delivery_days: days,
        message: message.trim(),
      });
      toast.success('Quote sent.');
      navigate(vertical.ownerDetailPath(requestId), { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Something went wrong. Try again.');
    } finally {
      setSending(false);
    }
  };

  if (loading) return <LoadingSpinner text="Loading…" />;

  return (
    <Page>
      <Back to={vertical.ownerDetailPath(requestId)}>← Back to request</Back>
      <Title>Send a quote</Title>
      <Summary>
        <SummaryTitle>{request?.offering?.title || 'Custom order'}</SummaryTitle>
        <div>
          Buyer budget {formatCustomizationMoney(request?.budget_min_cents)}
          {' – '}
          {formatCustomizationMoney(request?.budget_max_cents)}
        </div>
      </Summary>
      <form onSubmit={submit}>
        <Group>
          <Label htmlFor="price">Order price (EGP)</Label>
          <Input id="price" $error={Boolean(errors.price)} value={price} onChange={(event) => setPrice(event.target.value)} />
          {errors.price ? <Error>{errors.price}</Error> : null}
        </Group>
        <Group>
          <Label htmlFor="days">Estimated delivery (days)</Label>
          <Input id="days" $error={Boolean(errors.deliveryDays)} value={deliveryDays} onChange={(event) => setDeliveryDays(event.target.value)} />
          {errors.deliveryDays ? <Error>{errors.deliveryDays}</Error> : <Helper>How many days after acceptance.</Helper>}
        </Group>
        <Group>
          <Label htmlFor="message">Message (optional)</Label>
          <Textarea id="message" value={message} onChange={(event) => setMessage(event.target.value)} />
        </Group>
        <Note>Checkout fees, if any, are shown at checkout.</Note>
        <Actions>
          <Primary type="submit" disabled={sending}>{sending ? 'Sending…' : 'Send quote'}</Primary>
          <Cancel to={vertical.ownerDetailPath(requestId)}>Cancel</Cancel>
        </Actions>
      </form>
    </Page>
  );
}

const BoutiqueCustomizationQuotePage = () => <OfferingCustomizationQuotePage verticalKey="boutique" />;
export const DesignerCustomizationQuotePage = () => <OfferingCustomizationQuotePage verticalKey="designer" />;
export default BoutiqueCustomizationQuotePage;
